/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { decorate, computed, runInAction, observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon, Radio } from 'semantic-ui-react';
import c from 'classnames';
import { displayError } from '@amzn/base-ui/dist/helpers/notification';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreLoading, isStoreError, isStoreEmpty } from '@amzn/base-ui/dist/models/BaseStore';
import BasicProgressPlaceholder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import { gotoFn } from '@amzn/base-ui/dist/helpers/routing';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// - onPrevious (via props)
// - onNext (via props) a function is called with the selected computeTypeId
// - computePlatformsStore (via injection)
// - userStore (via injection)
class SelectComputePlatformStep extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.selectedPlatformId = undefined;
      this.processing = false;
    });
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    swallowError(this.computePlatformsStore.load());
  }

  goto(pathname) {
    const goto = gotoFn(this);
    goto(pathname);
  }

  get userStore() {
    return this.props.userStore;
  }

  get computePlatformsStore() {
    return this.props.computePlatformsStore;
  }

  handleSelectedComputeType = typeId => {
    this.selectedPlatformId = typeId;
  };

  handlePrevious = event => {
    event.preventDefault();
    event.stopPropagation();
    if (_.isFunction(this.props.onPrevious)) this.props.onPrevious();
  };

  handleNext = async event => {
    event.preventDefault();
    event.stopPropagation();
    if (_.isFunction(this.props.onNext)) {
      try {
        this.processing = true;
        await this.props.onNext(this.selectedPlatformId);
      } catch (error) {
        displayError(error);
      } finally {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  };

  render() {
    const store = this.computePlatformsStore;
    if (!store) return null;

    let content;
    if (isStoreError(store)) {
      content = this.renderLoadingError();
    } else if (isStoreLoading(store)) {
      content = <BasicProgressPlaceholder className="mt2" />;
    } else if (isStoreEmpty(store)) {
      content = this.renderEmpty();
    } else {
      content = this.renderContent();
    }

    return content;
  }

  renderContent() {
    const nextDisabled = _.isUndefined(this.selectedPlatformId);

    return (
      <div className="mt2 animated fadeIn">
        {this.renderCards()}
        {this.renderButtons({ nextDisabled })}
      </div>
    );
  }

  renderCards() {
    const processing = this.processing;
    const computeTypes = this.computePlatformsStore.list || [];
    const isSelected = type => type.id === this.selectedPlatformId;

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
        {_.map(computeTypes, type => (
          <div
            key={type.id}
            className={c('compute-card', { selected: isSelected(type) })}
            onClick={() => !processing && this.handleSelectedComputeType(type.id)}
          >
            <div className="compute-card-header">
              <Radio checked={isSelected(type)} disabled={processing} />
              <span className="compute-card-title">{type.title}</span>
            </div>
            <div className="compute-card-body">
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: type.descHtml }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderLoadingError() {
    const store = this.computePlatformsStore;
    return (
      <>
        <ErrorBox error={store.error} className="p0 mt2 mb3" />
        {this.renderButtons()}
      </>
    );
  }

  renderEmpty() {
    return (
      <>
        <div className="study-empty-state mt2">
          <Icon name="server" />
          <div className="study-empty-state-title">No compute platform</div>
          <div className="study-empty-state-text">
            There are no compute platforms to choose from. Your role might be restricted. Please contact your administrator.
          </div>
        </div>
        {this.renderButtons()}
      </>
    );
  }

  renderButtons({ nextDisabled = true } = {}) {
    const processing = this.processing;
    return (
      <div className="mt3" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <button
          className="workspace-action-btn workspace-action-btn-secondary"
          disabled={processing}
          onClick={this.handlePrevious}
        >
          <Icon name="left arrow" style={iconStyle} /> Previous
        </button>
        <button
          className="workspace-action-btn workspace-action-btn-primary"
          disabled={nextDisabled || processing}
          onClick={this.handleNext}
        >
          {processing ? 'Loading...' : 'Next'} <Icon name="right arrow" style={iconStyle} />
        </button>
      </div>
    );
  }
}

decorate(SelectComputePlatformStep, {
  handlePrevious: action,
  handleNext: action,
  handleSelectedComputeType: action,
  userStore: computed,
  computePlatformsStore: computed,
  processing: observable,
  selectedPlatformId: observable,
});

export default inject('userStore', 'computePlatformsStore')(withRouter(observer(SelectComputePlatformStep)));
